import React, { useEffect, forwardRef, useImperativeHandle } from "react";
import { Form, message, Modal, Row, Spin } from "antd";
import moment from "moment";

//metodos
import { getNewFolio, timbradoIndividual, getOrden, getTransaccion } from "./services";

//sections
import DocumentoFiscalSection from "./sections/DocumentoFiscalSection";
import RelacionFacturasSection from "./sections/RelacionFacturasSection";
import EmisorSection from "./sections/EmisorSection";
import ReceptorSection from "./sections/ReceptorSection";
import PagosProductosSection from "./sections/PagosProductosSection";
import ConceptosSection from "./sections/ConceptoSection";
import TotalesSection from "./sections/TotalesSection";
import RelacionTransaccionSection from "./sections/RelacionTransaccionSeccion";
import ComplementosPagoSection from "./sections/ComplementosPagoSection";

// Valores comunes a todos
const COMMON_DEFAULT_VALUES = {
    Fecha: moment(),
    Exportacion: "01",
    LugarExpedicion: "",
    Moneda: "MXN",
    TipoCambio: 1,
    FormaPago: "01",
    enviar_receptor: true,
    enviar_responsable: true,
    Emisor: {
        Nombre: "",
        Rfc: "",
        RegimenFiscal: ""
    },
    Receptor: {
        Nombre: "",
        Rfc: "",
        UsoCFDI: "",
        DomicilioFiscalReceptor: "",
        RegimenFiscalReceptor: ""
    },
    Conceptos: [],
    SubTotal: 0,
    Descuento: 0,
    Impuestos: {
        TotalImpuestosTrasladados: 0
    },
    Total: 0,
    Observaciones: ""
};

// Valores específicos por tipo
const DEFAULT_VALUES = {
    NC: {
        ...COMMON_DEFAULT_VALUES,
        Serie: process.env.REACT_APP_NOTA_CREDITO,
        TipoDeComprobante: "E",
        MetodoPago: "PUE",
        descuento: null,
        DescuentoPorcentual: 0,
    },
    PE: {
        ...COMMON_DEFAULT_VALUES,
        Serie: process.env.REACT_APP_COMPROBANTE_SERIE,
        TipoDeComprobante: "P",
        Moneda: "XXX",
        Complemento: {
            Any: [
                {
                    "Pago20:Pagos": {
                        Pago: [
                            {
                                FechaPago: moment(),
                                FormaDePagoP: "01",
                                MonedaP: "MXN",
                                Monto: "0.00",
                                NumOperacion: "1",
                                TipoCambioP: "1",
                                DoctoRelacionado: [
                                    
                                ],
                                ImpuestosP: {
                                    TrasladosP: [
                                        {
                                            "BaseP": "0.00",
                                            "ImpuestoP": "002",
                                            "TipoFactorP": "Tasa",
                                            "TasaOCuotaP": "0.160000",
                                            "ImporteP": "0.00"
                                        }
                                    ]
                                },
                            },
                        ],
                        Totales: {
                            TotalTrasladosBaseIVA16: "0.00",
                            TotalTrasladosImpuestoIVA16: "0.00",
                            MontoTotalPagos: "0.00",
                        },
                    }
                }
            ]
        }
    }
};

/**
 * @description Este componente es el formulario que se muestra dentro del drawer de facturación,
 * aquí se capturan los datos necesarios para el timbrado de la factura.
 * @param {*} props
 * @param {*} ref - Referencia al formulario
 * @returns
 */
const FacturaTimbradoForm = forwardRef(({ 
    tipoFactura = "NC",
    disabled = false,
    cliente = null,
    clienteId = null,
    transaccionId = null,
    ordenId = null,
    onCloseEvent = () => {}
}, ref) => {

    const [transaccion, setTransaccion] = React.useState(null);
    const [loading, setLoading] = React.useState(false);

    useEffect(() => {
        getFolio();

        if(ordenId) getOrdenData();
        if(transaccionId) getTransaccionData();
    }, []);

    const getFolio = async () => {
        const folio = await getNewFolio(tipoFactura);
        if (folio && ref?.current) {
            ref.current.setFieldsValue({ Folio: folio });
        }
    };

    // Función para obtener datos de la orden y setearlos en el formulario
    const getOrdenData = async () => {
       const orden = await getOrden(ordenId);
       if(ref?.current && orden) {
            ref.current.setFieldsValue({
                Folio: (orden.folio_number).toString().padStart(7, "0"), 
                Receptor: {
                    Nombre: orden.cliente_id?.razon_social || "",
                    Rfc: orden.cliente_id?.rfc || "",
                    DomicilioFiscalReceptor: orden.cliente_id?.codigo_postal || "",
                    RegimenFiscalReceptor: orden.cliente_id?.regimen_fiscal || ""
                }
            });
       }
    }

    const getTransaccionData = async () => {
        const transaccion = await getTransaccion(transaccionId);
        if(ref?.current && transaccion) {

            try{
                setTransaccion(transaccion);
                let {monto, monto_dolar, _id} = transaccion;
                const montoFormateado = Number(monto || 0).toFixed(2);
                let Conceptos = [
                    {
                        "ClaveProdServ": "84111506",
                        "Cantidad": "1",
                        "ClaveUnidad": "ACT",
                        "Descripcion": "Pago",
                        "ValorUnitario": "0",
                        "Importe": "0",
                        "ObjetoImp": "01"
                    }
                ]
                let newData = {
                    Conceptos,
                    Complemento: {
                        Any: [
                            {
                                "Pago20:Pagos": {
                                    Pago: [
                                        {
                                            FechaPago: moment(transaccion.createdAt),
                                            FormaDePagoP: "01",
                                            MonedaP: transaccion.moneda || "MXN",
                                            Monto: montoFormateado,
                                            NumOperacion: transaccion.numero_operacion || "1",
                                            TipoCambioP: "1",
                                            DoctoRelacionado: [],
                                            ImpuestosP: {
                                                TrasladosP: [
                                                    {
                                                        BaseP: "0.00",
                                                        ImpuestoP: "002",
                                                        TipoFactorP: "Tasa",
                                                        TasaOCuotaP: "0.160000",
                                                        ImporteP: "0.00"
                                                    }
                                                ]
                                            },
                                        }
                                    ],
                                    Totales: {
                                        TotalTrasladosBaseIVA16: "0.00",
                                        TotalTrasladosImpuestoIVA16: "0.00",
                                        MontoTotalPagos: montoFormateado,
                                    },
                                }
                            }
                        ]
                    }
                }

                ref.current.setFieldsValue(newData);
    
            }catch(error){
                console.error("Error al obtener datos de la transacción:", error);
            }
        }
    }


    /**
     * @description Maneja el submit del formulario de timbrado
     * @param {*} values - Valores del formulario
     */
    const handleFinish = async (values) => {
        try {
            setLoading(true);
            let dataToSubmit = {
                ...values, //valores del formulario
                cliente_id: clienteId,
                transaccion_id: transaccionId,
                orden_id: ordenId
            }

            let response = await timbradoIndividual(dataToSubmit)
            message.success("Factura timbrada exitosamente");
            onCloseEvent(true)
        } catch (error) {
            setLoading(false);
            console.error("Error en el timbrado individual:", error.response);
            if (Array.isArray(error.response?.data?.data)) {
                Modal.error({
                    title: 'Error al timbrar la factura',
                    content: <ul>
                        {error.response.data.data.map((e, index) => (
                            <li key={index}>
                                {typeof e === 'object' ? JSON.stringify(e) : e}
                            </li>
                        ))}
                    </ul>
                })
            } else {
                message.error('Error al timbrar la factura. Por favor, intente nuevamente.')
            }
        }

    };

    /**
     * @description Maneja los errores de validación del formulario
     * @param {*} errorInfo - Información de los errores
     */
    const handleFinishFailed = (errorInfo) => {
        console.error("Errores en el formulario:", errorInfo);
    };

    
    return (
        <Form
            ref={ref}
            name="timbrado-factura"
            layout="vertical"
            initialValues={DEFAULT_VALUES[tipoFactura]}
            onFinish={handleFinish}
            onFinishFailed={handleFinishFailed}
            scrollToFirstError={true}
        >
            <Row justify="center" align="center">
                <img
                    src="/images/sat.svg"
                    alt="SAT - Servicio de Administración Tributaria"
                    width={"100%"}
                    style={{
                        marginTop: "2em",
                        marginBottom: "4em",
                        maxWidth: 350,
                    }}
                />
                <small>v 2.0</small>
            </Row>
            <Spin spinning={loading} tip="Cargando..." size="large">    
                <DocumentoFiscalSection tipoFactura={tipoFactura}/>

                {tipoFactura === "NC" && <RelacionFacturasSection 
                    clienteId={cliente?._id}
                />}

                {(tipoFactura === "PE" &&  transaccion) && <RelacionTransaccionSection 
                    transaccionObj={transaccion}
                    clienteId={clienteId}
                />}

                <EmisorSection/>

                <ReceptorSection cliente={cliente} />

                <PagosProductosSection />

                <ConceptosSection hasTransaccion={Boolean(transaccionId)}/>

                {tipoFactura === "PE" && <ComplementosPagoSection transaccionObj={transaccion} />}

                <TotalesSection tipoFactura={tipoFactura}/>
            </Spin>
        </Form>
    );
});

export default FacturaTimbradoForm;
